import Select from '@mui/material/Select';
import { useEffect, useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { makeStyles } from '@mui/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  CustomEntityFilters,
  CustomEntityKindsFilter,
  useClearEntityFilters,
} from '@mb.io/core-components';
import {
  EntityKindFilter,
  EntityOrderFilter,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import { useAllowedEntityKinds } from '../../hooks/useAllowedEntityKinds';

const iconComponent = (iconProps: any) => <ExpandMoreIcon {...iconProps} />;

export function filterKinds(
  allKinds: string[],
  forcedKinds?: string,
): Record<string, string> {
  let availableKinds = allKinds;
  if (
    forcedKinds &&
    !allKinds.some(
      a =>
        a.toLocaleLowerCase('en-US') === forcedKinds.toLocaleLowerCase('en-US'),
    )
  ) {
    availableKinds = availableKinds.concat([forcedKinds]);
  }

  return availableKinds.reduce(
    (acc, kind) => {
      acc[kind.toLocaleLowerCase('en-US')] = kind;
      return acc;
    },
    {} as Record<string, string>,
  );
}

export interface EntityKindPickerProps {
  hidden?: boolean;
}

const useStyles = makeStyles(theme => ({
  select: {
    width: '100%',
    maxHeight: '50px',
    borderRadius: '2px',
    borderBottom:
      theme.palette.mode === 'dark'
        ? '1px solid rgba(255, 255, 255, 0.70)'
        : '1px solid rgba(0, 0, 0, 0.60)',
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.12)'
        : 'rgba(0, 0, 0, 0.08)',
    '&:hover': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.16)'
          : 'rgba(0, 0, 0, 0.16)',
    },
  },
}));

export const EntityKindPicker = (props: Readonly<EntityKindPickerProps>) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { hidden } = props;
  const { clearFilterState, resetFilters } = useClearEntityFilters();

  const { allowedKinds } = useAllowedEntityKinds();

  const {
    filters,
    updateFilters,
    queryParameters: { kind: kindParameter },
  } = useEntityList<CustomEntityFilters>();

  const kinds = ['All', ...allowedKinds];

  const [selectedKind, setSelectedKind] = useState<string>(
    Array.isArray(kindParameter)
      ? kindParameter[0]
      : (kindParameter ?? filters.kind?.value ?? ''),
  );

  const options = filterKinds(kinds, selectedKind);

  const items = Object.keys(options).map(key => ({
    value: key,
    label: options[key],
  }));

  const getValue = () => {
    if (selectedKind === '' || !selectedKind) {
      return 'all';
    }

    return selectedKind.toLowerCase();
  };

  useEffect(() => {
    if (clearFilterState.kind) {
      setSelectedKind('');
    }
  }, [clearFilterState, setSelectedKind]);

  useEffect(() => {
    if (selectedKind) {
      updateFilters({
        kind: selectedKind
          ? new EntityKindFilter(selectedKind, 'Kind')
          : undefined,
        kinds: undefined,
        order: new EntityOrderFilter([['kind', 'asc']]),
      });
    } else {
      updateFilters({
        kind: undefined,
        kinds: new CustomEntityKindsFilter(allowedKinds),
        order: new EntityOrderFilter([['kind', 'asc']]),
        type: undefined,
      });
    }
  }, [allowedKinds, selectedKind, updateFilters]);

  return hidden ? null : (
    <FormControl variant="filled" sx={{ width: '100%' }}>
      <InputLabel id="entity-kind-picker-label">Kind</InputLabel>
      <Select
        labelId="entity-kind-picker-label"
        id="entity-kind-picker"
        value={getValue()}
        IconComponent={(iconProps: any) => iconComponent(iconProps)}
        onChange={event => {
          const value = event.target.value;
          resetFilters('kind');
          setSelectedKind(value !== 'all' ? value : '');
        }}
        className={classes.select}
      >
        {items?.map(item => (
          <MenuItem key={`${item.value}-${item.label}`} value={item.value}>
            {item.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
